import {create} from 'zustand'
import {persist} from 'zustand/middleware'

import {getCreatableNameByBlock} from '../utils'

import type {Mutate, StoreApi} from 'zustand'
import type {PanelFragment, XcNwBlockFragment} from '@sdox/api'

export type EditorClipboardData = {
  type: 'block'
  block: XcNwBlockFragment
  panelMap: Record<string, PanelFragment>
  name: string
}

interface EditorClipboardStoreState {
  data: EditorClipboardData[]
  addEntry: (data: Omit<EditorClipboardData, 'name'>) => void
  deleteEntry: (index: number) => void
  renameEntry: (index: number, name: string) => void
}

type StoreWithPersist = Mutate<StoreApi<EditorClipboardStoreState>, [['zustand/persist', EditorClipboardStoreState]]>

export const withStorageDOMEvents = (store: StoreWithPersist) => {
  const storageEventCallback = (e: StorageEvent) => {
    if (e.key === store.persist.getOptions().name && e.newValue) {
      store.persist.rehydrate()
    }
  }

  window.addEventListener('storage', storageEventCallback)

  return () => {
    window.removeEventListener('storage', storageEventCallback)
  }
}

export const useEditorClipboardStore = create<EditorClipboardStoreState>()(
  persist(
    (set) => ({
      data: [],
      addEntry: (data) => {
        set(state => ({
          data: [
            ...state.data,
            {...data, name: `${getCreatableNameByBlock(data.block)} #${data.block.id}`},
          ],
        }))
      },
      deleteEntry: (index: number) => {
        set(state => {
          const copy = [...state.data]
          copy.splice(index, 1)
          return {data: copy}
        })
      },
      renameEntry: (index: number, name: string) => {
        set(state => {
          const data = [...state.data].map((entry, i) => (
            index !== i ? entry : {...entry, name}
          ))
          return {data}
        })
      },
    }),
    {
      name: 'editor-clipboard',
      version: 3,
    },
  ),
)

withStorageDOMEvents(useEditorClipboardStore)
